/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { KillswitchCapabilityKey } from "../models/components/killswitchcapabilitykey.js";
import { KillswitchListStatus } from "../models/components/killswitchliststatus.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  KillswitchesListRequest,
  KillswitchesListSecurity,
} from "../models/operations/killswitcheslist.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildKillswitchesInfiniteQuery,
  buildKillswitchesQuery,
  KillswitchesInfiniteQueryData,
  KillswitchesPageParams,
  KillswitchesQueryData,
  prefetchKillswitches,
  prefetchKillswitchesInfinite,
  queryKeyKillswitches,
  queryKeyKillswitchesInfinite,
} from "./killswitches.core.js";
export {
  buildKillswitchesInfiniteQuery,
  buildKillswitchesQuery,
  type KillswitchesInfiniteQueryData,
  type KillswitchesPageParams,
  type KillswitchesQueryData,
  prefetchKillswitches,
  prefetchKillswitchesInfinite,
  queryKeyKillswitches,
  queryKeyKillswitchesInfinite,
};

export type KillswitchesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list killswitches
 */
export function useKillswitches(
  security: KillswitchesListSecurity,
  request?: KillswitchesListRequest | undefined,
  options?: QueryHookOptions<KillswitchesQueryData, KillswitchesQueryError>,
): UseQueryResult<KillswitchesQueryData, KillswitchesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildKillswitchesQuery(
      client,
      security,
      request,
      options,
    ),
    ...options,
  });
}

/**
 * list killswitches
 */
export function useKillswitchesSuspense(
  security: KillswitchesListSecurity,
  request?: KillswitchesListRequest | undefined,
  options?: SuspenseQueryHookOptions<
    KillswitchesQueryData,
    KillswitchesQueryError
  >,
): UseSuspenseQueryResult<KillswitchesQueryData, KillswitchesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildKillswitchesQuery(
      client,
      security,
      request,
      options,
    ),
    ...options,
  });
}

/**
 * list killswitches
 */
export function useKillswitchesInfinite(
  security: KillswitchesListSecurity,
  request?: KillswitchesListRequest | undefined,
  options?: InfiniteQueryHookOptions<
    KillswitchesInfiniteQueryData,
    KillswitchesQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<KillswitchesInfiniteQueryData, KillswitchesPageParams>,
  KillswitchesQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    KillswitchesInfiniteQueryData,
    KillswitchesQueryError,
    InfiniteData<KillswitchesInfiniteQueryData, KillswitchesPageParams>,
    QueryKey,
    KillswitchesPageParams
  >({
    ...buildKillswitchesInfiniteQuery(
      client,
      security,
      request,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * list killswitches
 */
export function useKillswitchesInfiniteSuspense(
  security: KillswitchesListSecurity,
  request?: KillswitchesListRequest | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    KillswitchesInfiniteQueryData,
    KillswitchesQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<KillswitchesInfiniteQueryData, KillswitchesPageParams>,
  KillswitchesQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    KillswitchesInfiniteQueryData,
    KillswitchesQueryError,
    InfiniteData<KillswitchesInfiniteQueryData, KillswitchesPageParams>,
    QueryKey,
    KillswitchesPageParams
  >({
    ...buildKillswitchesInfiniteQuery(
      client,
      security,
      request,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setKillswitchesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      capabilityKey?: KillswitchCapabilityKey | undefined;
      userId?: string | undefined;
      status?: KillswitchListStatus | undefined;
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: KillswitchesQueryData,
): KillswitchesQueryData | undefined {
  const key = queryKeyKillswitches(...queryKeyBase);

  return client.setQueryData<KillswitchesQueryData>(key, data);
}

export function invalidateKillswitches(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      capabilityKey?: KillswitchCapabilityKey | undefined;
      userId?: string | undefined;
      status?: KillswitchListStatus | undefined;
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "killswitches", "list", ...queryKeyBase],
  });
}

export function invalidateAllKillswitches(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "killswitches", "list"],
  });
}
